<template>
  <div>
    <el-dialog
      title="License Activation"
      :visible.sync="licenseDialog"
      width="40%"
      center
      lock-scroll
      append-to-body
      :show-close="true"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <div>
        <el-tabs type="card" stretch v-model="lc.type">
          <el-alert
            v-if="status.message"
            :type="status.type || 'info'"
            effect="dark"
            v-html="status.message"
            show-icon
            closable
            class="mb-1"
          />
          <el-tab-pane name="token" lazy :disabled="loading">
            <span slot="label">
              <i class="el-icon-message" />
              Activation code
            </span>
            <el-input
              ref="l_token"
              autofocus
              rows="5"
              v-model="lc.activation_token"
              type="textarea"
              placeholder="Paste activation code here"
              name="activation_token"
            />
            <div class="mt-2">
              <el-button :loading="loading" type="primary" @click="activate">
                Activate
              </el-button>
            </div>
          </el-tab-pane>
          <el-tab-pane name="account" lazy :disabled="loading">
            <span slot="label">
              <i class="el-icon-user" />
              Salamanda Account
            </span>
            <el-form label-width="120px">
              <el-form-item label="Application secret">
                <el-input type="text" v-model.trim="lc.secret" readonly />
              </el-form-item>
              <el-form-item label="Username">
                <el-input ref="l_username" type="text" v-model="lc.username" />
              </el-form-item>
              <el-form-item label="Password">
                <el-input type="password" v-model="lc.password" />
              </el-form-item>
              <el-form-item class="justify-content-end pull-right">
                <el-button
                  :loading="loading"
                  round
                  type="primary"
                  @click="activate"
                >
                  Sign In
                </el-button>
              </el-form-item>
            </el-form>
          </el-tab-pane>
        </el-tabs>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import axios from 'axios'

// Centralised HTTP client for license operations.
const createLicenseHttpClient = () =>
  axios.create({
    baseURL: 'http://license.salamanda.org/',
    timeout: 5000,
    headers: { Accept: 'application/json' }
  })

export default {
  name: 'OnboardBase',

  data () {
    return {
      dialogMessage: 'Please, enter address to your hosted server.',
      licenseDialog: false,
      lc: {
        secret: 'salamanda-v2',
        type: 'account',
        activation_token: '',
        username: 'test-server',
        password: 'Password!'
      },
      status: {
        type: '',
        message: null
      },
      loading: false
    }
  },

  computed: {
    ...mapState({
      baseUrl: state => state.Base.BASE_URL
    })
  },

  watch: {
    'lc.type' (value) {
      // Delay focus until after the tab content is rendered.
      setTimeout(() => {
        if (value === 'token' && this.$refs.l_token) {
          this.$refs.l_token.focus()
        } else if (value === 'account' && this.$refs.l_username) {
          this.$refs.l_username.focus()
        }
      }, 800)
    }
  },

  methods: {
    ...mapActions('Base', ['setBaseURL']),

    showURLDialog () {
      this.$prompt(this.dialogMessage, 'SALAMANDA SERVER NOT SET!', {
        confirmButtonText: 'Verify',
        showClose: false,
        closeOnClickModal: false,
        showCancelButton: false,
        confirmButtonClass: 'el-button--primary',
        closeOnPressEscape: false,
        distinguishCancelAndClose: true,
        // inputPattern: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6}|[0-9]{1,3})([/\w .-]*)*\/?$/, // @todo verify url regex
        inputErrorMessage: 'Please, enter a valid URL',
        inputValue: '',
        center: true,
        type: 'warning',
        beforeClose: (action, instance, done) => {
          done()
        }
      })
        .then(({ value }) =>
          this.$http.post(
            'ping',
            {},
            {
              baseURL: value
            }
          )
            .then(res => res.data)
            .then(data => {
              if (data.data === 'pong') {
                this.setBaseURL(value)
                this.verifyLicense()
              } else {
                this.showURLDialog()
              }
            })
            .catch(() => {
              this.dialogMessage =
                'Incorrect server address or server is unreachable. Please, contact the system administrator.'
              this.showURLDialog()
            })
        )
    },

    getErrorMessage (err, defaultMessage) {
      if (err && err.response && err.response.data && err.response.data.message) {
        return err.response.data.message
      }
      if (err && err.response && (err.response.status || err.response.request)) {
        return `Error occurred with status: ${
          err.response.status || err.response.request
        }`
      }
      return defaultMessage
    },

    setStatus (type, message) {
      this.status.type = type
      this.status.message = message
    },

    async activate () {
      this.status.message = null
      this.loading = true

      const http = createLicenseHttpClient()

      try {
        await http.get('sanctum/csrf-cookie')

        const loginResponse = await http.post('api/v1/login', this.lc)
        const loginData = loginResponse.data

        const licenseResponse = await http.get('api/v1/license', {
          headers: {
            Authorization: `Bearer ${loginData.data.token}`
          }
        })

        const license = licenseResponse.data

        this.setStatus(
          'success',
          `License found: ${license.client.name} (expires: ${license.end_date})`
        )

        try {
          await this.$authService.setLicense(license, this.baseUrl)
          this.$notify.success('License set successfully.')
          this.loading = false

          const loader = this.$loading({
            text: 'Starting application...'
          })

          setTimeout(() => {
            loader.close()
            this.$router.push({ name: 'login' })
          }, 2000)
        } catch (err) {
          this.setStatus(
            'warning',
            this.getErrorMessage(
              err,
              'An error occurred. Please, check your computer settings and try again.'
            )
          )
          this.loading = false
        }
      } catch (err) {
        if (err && err.response && err.response.status === 502) {
          this.setStatus(
            'warning',
            'You do not have any active license at the moment. Please, contact support for assistance.'
          )
        } else {
          this.setStatus('error', this.getErrorMessage(err, 'License activation failed.'))
        }
        this.loading = false
      }
    },

    async verifyLicense () {
      try {
        await this.$authService.verifyLicense()

        const loader = this.$loading({
          text: 'Successful! Redirecting you to login resource...'
        })

        setTimeout(() => {
          loader.close()
          this.$router.push({ name: 'login' })
        }, 2000)
      } catch (err) {
        // Ensure any existing global dialogs/messages are closed so they don't
        // visually or interactively overlap the license activation dialog.
        if (this.$msgbox && typeof this.$msgbox.close === 'function') {
          this.$msgbox.close()
        }
        if (this.$message && typeof this.$message.closeAll === 'function') {
          this.$message.closeAll()
        }

        this.licenseDialog = true
        this.setStatus(
          'warning',
          this.getErrorMessage(err, 'Invalid license')
        )

        throw err
      }
    }
  },

  async mounted () {
    if (!this.baseUrl || !this.baseUrl.length) {
      this.showURLDialog()
    } else {
      await this.verifyLicense()
    }
  }
}
</script>

<style scoped>
</style>
